<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useTosterStore } from './store.ts';
import Wrapper from '@/shared/components/core/TosterCenter/Wrapper.vue';
import { useMainStore } from '@/core/store/main.ts';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import type { Tost } from './types.ts';

const { visibleTosts } = storeToRefs(useTosterStore());
const { removeTost } = useTosterStore();
const router = useRouter();

const { desktopSlideContainerWidth } = storeToRefs(useMainStore());

const maxTostContainerWidthPx = computed(() => {
  return desktopSlideContainerWidth.value ? `${desktopSlideContainerWidth.value}px` : '100%';
});

const handleTostActivate = (tost: Tost) => {
  if (!tost.to) {
    return;
  }

  removeTost(tost.id);
  void router.push(tost.to);
};

</script>

<template>
  <TransitionGroup
    id="tostsCotainer"
    name="list"
    class="tosts-conteiner"
    tag="div"
  >
    <div
      v-for="tost in visibleTosts"
      :key="tost.id"
      class="tost"
      :class="{ 'tost--clickable': !!tost.to }"
      :role="tost.to ? 'link' : undefined"
      :tabindex="tost.to ? 0 : undefined"
      :aria-label="tost.to ? (tost.title || undefined) : undefined"
      @click="handleTostActivate(tost)"
      @keydown.enter="handleTostActivate(tost)"
    >
      <component
        :is="tost.component"
        v-if="tost.component"
      />
      <Wrapper
        v-else
        :key="tost.id"
        :close-delay="tost.closeDelay"
      >
        <div class="content">
          <component
            :is="tost.icon"
            v-if="tost.icon"
            class="prefix-icon"
          />
          <div class="text">
            <div
              v-if="tost.title"
              class="title"
            >
              {{ tost.title }}
            </div>
            <div
              v-if="tost.description"
              class="description"
            >
              {{ tost.description }}
            </div>
          </div>
        </div>
      </Wrapper>
    </div>
  </TransitionGroup>
  <!-- </div> -->
</template>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
  max-height: 500px;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-100px);
  max-height: 0px;
}

.tosts-conteiner {
  width: v-bind(maxTostContainerWidthPx);
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 900;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  row-gap: 20px;
  margin: auto;

  @media all and (min-width: 601px) {
    width: calc(v-bind(maxTostContainerWidthPx) / var(--zoom-for-desktop));
  }
}

.tost {
  width: 100%;
}

.tost--clickable {
  cursor: pointer;
}

.content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 12px;
}

.prefix-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.title {
  font-family: var(--fontFamily);
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: rgba(255, 255, 255, 1);
}

.description {
  font-family: var(--fontFamily);
  font-weight: 400;
  font-size: 11px;
  line-height: 130%;
  color: rgba(139, 139, 138, 1);
}

.title + .description {
  margin-top: 4px;
}
</style>
